<template>
    <div class="homepage-domov-generic">
        <!-- Hero Section for Anonymous Users -->
        <section class="hero-section relative overflow-hidden bg-white dark:bg-surface-card">
            <!-- Hero Content -->
            <div class="relative container mx-auto px-5 lg:px-20 pt-10 pb-12 md:pt-[120px] md:pb-[80px] grid items-center gap-12 lg:grid-cols-2">
                <div class="flex-1 min-w-0 relative z-10 max-w-[640px]">
                    <!-- Main Heading -->
                    <h1
                        class="font-heading font-bold text-[40px] md:text-[56px] leading-[1.1] tracking-tight text-gray-900 dark:text-white mb-4"
                    >
                        Miesto pre osobnostný rast a budovanie lepšej budúcnosti
                    </h1>

                    <!-- Subtitle -->
                    <p class="font-sans text-[18px] leading-relaxed text-gray-600 dark:text-gray-300 mb-8">
                        Nájdi príležitosti pre seba a zlepši Slovensko.
                    </p>

                    <!-- CTA Buttons -->
                    <div class="flex flex-col sm:flex-row flex-wrap gap-4">
                        <NuxtLink
                            to="/auth/sign-up"
                            class="bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-full px-6 py-3 flex items-center justify-center gap-2 font-sans font-semibold text-base whitespace-nowrap hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
                        >
                            <span>Registrácia</span>
                            <span class="text-xl">→</span>
                        </NuxtLink>
                        <NuxtLink
                            to="/sebapoznanie"
                            class="bg-white dark:bg-surface-card border-2 border-gray-900 dark:border-white text-gray-900 dark:text-white rounded-full px-6 py-3 flex items-center justify-center gap-2 font-sans font-semibold text-base whitespace-nowrap hover:bg-gray-50 dark:hover:bg-surface-card transition-colors"
                        >
                            <span>Objav sebapoznanie</span>
                        </NuxtLink>
                    </div>

                    <!-- Secondary audience: a text link, so the two primary buttons fit on one row -->
                    <NuxtLink
                        to="#pre-organizacie-actions"
                        class="mt-5 inline-flex items-center gap-2 font-sans font-semibold text-base text-orange-700 dark:text-orange-300 hover:underline"
                    >
                        <span>Pre organizácie</span>
                        <span aria-hidden="true">→</span>
                    </NuxtLink>
                </div>

                <!-- Hands with Flower Illustration (Right side) -->
                <div
                    class="hidden lg:block w-full aspect-[3/2] pointer-events-none dark:rounded-[2rem] dark:bg-[#f4efe5] dark:p-5"
                >
                    <img
                        src="/images/sprout_transparent.webp"
                        alt="Hands holding soil with a growing flower"
                        width="625"
                        height="417"
                        fetchpriority="high"
                        decoding="async"
                        class="size-full object-contain dark:mix-blend-multiply"
                    />
                </div>
            </div>
        </section>

        <!-- Statistics Section -->
        <StatisticsSection />

        <!-- Latest Opportunities Section -->
        <section class="py-16 px-5 lg:px-20 bg-white dark:bg-surface-card">
            <div class="container mx-auto max-w-[1440px]">
                <div class="mb-8 text-center">
                    <h2 class="font-semibold text-4xl text-gray-900 dark:text-white mb-2">Najnovšie príležitosti</h2>
                    <p class="text-base text-gray-600 dark:text-gray-400">
                        Objavuj to najzaujímavejšie z každej kategórie
                    </p>
                </div>

                <!-- Category Cards Grid -->
                <CategoryCardsGrid />
            </div>
        </section>

        <OrganizationActionCards framed />

        <!-- Latest Mentors -->
        <section class="py-16 px-5 lg:px-20 bg-gray-50 dark:bg-surface-card">
            <div class="container mx-auto max-w-[1440px]">
                <div class="mb-8 flex items-center justify-between">
                    <div>
                        <h2 class="font-semibold text-3xl text-gray-900 dark:text-white mb-2">Najnovší mentori</h2>
                        <p class="text-base text-gray-600 dark:text-gray-400">
                            Spojte sa s novými profesionálmi, ktorí vám pomôžu rásť
                        </p>
                    </div>
                    <NuxtLink
                        to="/mentori"
                        class="hidden md:flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold transition-colors"
                    >
                        <span>Všetci mentori</span>
                        <span>→</span>
                    </NuxtLink>
                </div>

                <div class="overflow-x-auto scrollbar-hide -mx-5 px-5 lg:mx-0 lg:px-0 lg:overflow-visible">
                    <div :class="cardRowClass">
                        <HomepageMentorCard v-for="mentor in mentors" :key="mentor.name" :mentor="mentor" />
                    </div>
                </div>
            </div>
        </section>

        <!-- Latest Jobs -->
        <section class="py-16 px-5 lg:px-20 bg-white dark:bg-surface-card">
            <div class="container mx-auto max-w-[1440px]">
                <div class="mb-8 flex items-center justify-between">
                    <div>
                        <h2 class="font-semibold text-3xl text-gray-900 dark:text-white mb-2">
                            Najnovšie pracovné ponuky
                        </h2>
                        <p class="text-base text-gray-600 dark:text-gray-400">
                            Príležitosti od slovenských organizácií
                        </p>
                    </div>
                    <NuxtLink
                        to="/praca"
                        class="hidden md:flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold transition-colors"
                    >
                        <span>Všetky ponuky</span>
                        <span>→</span>
                    </NuxtLink>
                </div>

                <div class="overflow-x-auto scrollbar-hide -mx-5 px-5 lg:mx-0 lg:px-0 lg:overflow-visible">
                    <div :class="cardRowClass">
                        <HomepageJobCard v-for="job in jobs" :key="job.title" :job="job" />
                    </div>
                </div>
            </div>
        </section>

        <!-- Volunteer Projects -->
        <section class="py-16 px-5 lg:px-20 bg-gray-50 dark:bg-surface-card">
            <div class="container mx-auto max-w-[1440px]">
                <div class="mb-8 flex items-center justify-between">
                    <div>
                        <h2 class="font-semibold text-3xl text-gray-900 dark:text-white mb-2">
                            Dobrovoľnícke projekty
                        </h2>
                        <p class="text-base text-gray-600 dark:text-gray-400">Prispej k lepšej spoločnosti</p>
                    </div>
                    <NuxtLink
                        to="/dobrovolnictvo"
                        class="hidden md:flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold transition-colors"
                    >
                        <span>Všetky projekty</span>
                        <span>→</span>
                    </NuxtLink>
                </div>

                <div class="overflow-x-auto scrollbar-hide -mx-5 px-5 lg:mx-0 lg:px-0 lg:overflow-visible">
                    <div :class="cardRowClass">
                        <HomepageVolunteerCard
                            v-for="volunteer in volunteers"
                            :key="volunteer.title"
                            :volunteer="volunteer"
                        />
                    </div>
                </div>
            </div>
        </section>

        <!-- Education Programs -->
        <section class="py-16 px-5 lg:px-20 bg-white dark:bg-surface-card">
            <div class="container mx-auto max-w-[1440px]">
                <div class="mb-8 flex items-center justify-between">
                    <div>
                        <h2 class="font-semibold text-3xl text-gray-900 dark:text-white mb-2">Vzdelávacie programy</h2>
                        <p class="text-base text-gray-600 dark:text-gray-400">Rozvíjaj svoje zručnosti</p>
                    </div>
                    <NuxtLink
                        to="/vzdelavanie"
                        class="hidden md:flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold transition-colors"
                    >
                        <span>Všetky programy</span>
                        <span>→</span>
                    </NuxtLink>
                </div>

                <div class="overflow-x-auto scrollbar-hide -mx-5 px-5 lg:mx-0 lg:px-0 lg:overflow-visible">
                    <div :class="cardRowClass">
                        <HomepageEducationCard
                            v-for="education in educationItems"
                            :key="education.title"
                            :education="education"
                        />
                    </div>
                </div>
            </div>
        </section>

        <!-- Latest Discussions -->
        <DiscussionHomepageSection />

        <!-- Latest Blog Articles -->
        <HomepageBlogSection />

        <!-- Why Growni Section -->
        <section
            class="py-16 px-5 lg:px-20 bg-gradient-to-br from-purple-50 to-white dark:from-purple-950 dark:to-gray-900"
        >
            <div class="container mx-auto max-w-[1440px]">
                <div class="text-center mb-12">
                    <h2 class="font-semibold text-4xl text-gray-900 dark:text-white mb-4">Prečo GROWNi?</h2>
                    <p class="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                        Všetko, čo potrebuješ pre osobný a profesionálny rast na jednom mieste
                    </p>
                </div>

                <div class="grid gap-8 md:grid-cols-3">
                    <!-- Feature 1 -->
                    <div
                        class="bg-white dark:bg-surface-card rounded-2xl p-8 text-center shadow-sm hover:shadow-md transition-shadow"
                    >
                        <div class="text-5xl mb-4">🎯</div>
                        <h3 class="font-sans font-semibold text-xl text-gray-900 dark:text-white mb-3">
                            Personalizované odporúčania
                        </h3>
                        <p class="text-gray-600 dark:text-gray-400">
                            Absolvuj testy sebapoznania a dostávaj príležitosti prispôsobené tvojmu profilu
                        </p>
                    </div>

                    <!-- Feature 2 -->
                    <div
                        class="bg-white dark:bg-surface-card rounded-2xl p-8 text-center shadow-sm hover:shadow-md transition-shadow"
                    >
                        <div class="text-5xl mb-4">🤝</div>
                        <h3 class="font-sans font-semibold text-xl text-gray-900 dark:text-white mb-3">
                            Overení mentori
                        </h3>
                        <p class="text-gray-600 dark:text-gray-400">
                            Spoj sa s profesionálmi, ktorí majú reálne skúsenosti vo svojich oblastiach
                        </p>
                    </div>

                    <!-- Feature 3 -->
                    <div
                        class="bg-white dark:bg-surface-card rounded-2xl p-8 text-center shadow-sm hover:shadow-md transition-shadow"
                    >
                        <div class="text-5xl mb-4">📈</div>
                        <h3 class="font-sans font-semibold text-xl text-gray-900 dark:text-white mb-3">
                            Sleduj svoj pokrok
                        </h3>
                        <p class="text-gray-600 dark:text-gray-400">
                            Definuj si ciele a sleduj svoj osobný a profesionálny rast v čase
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Partner Logos -->
        <PartnerLogos />

        <!-- Final CTA -->
        <section
            class="py-20 px-5 lg:px-20 bg-gradient-to-r from-purple-600 to-blue-600 dark:from-purple-700 dark:to-blue-700 text-white"
        >
            <div class="container mx-auto max-w-[1440px] text-center">
                <h2 class="font-sans font-bold text-4xl mb-4">Pripravený začať svoju cestu rastu?</h2>
                <p class="font-sans text-xl mb-8 opacity-90">Pridaj sa k tisíckam ľudí, ktorí už rastú s GROWNi</p>
                <NuxtLink
                    to="/auth/sign-up"
                    class="inline-flex bg-white dark:bg-surface-card text-purple-600 dark:text-white rounded-full px-8 py-4 items-center gap-2 font-sans font-semibold text-lg hover:bg-gray-100 dark:hover:bg-surface-card transition-colors"
                >
                    <span>Registrovať sa zadarmo</span>
                    <span class="text-xl">→</span>
                </NuxtLink>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import HomepageMentorCard from '../HomepageMentorCard.vue'
import HomepageJobCard from '../HomepageJobCard.vue'
import HomepageVolunteerCard from '../HomepageVolunteerCard.vue'
import HomepageEducationCard from '../HomepageEducationCard.vue'
import HomepageBlogSection from '../HomepageBlogSection.vue'
import StatisticsSection from '../StatisticsSection.vue'
import PartnerLogos from '../PartnerLogos.vue'
import CategoryCardsGrid from '../CategoryCardsGrid.vue'
import OrganizationActionCards from '../OrganizationActionCards.vue'

// Scroll row on mobile; from lg a grid of the first 3 (xl: 4) cards, so nothing is cut off at the edge
const cardRowClass =
    'flex gap-6 pb-4 lg:grid lg:grid-cols-3 xl:grid-cols-4 lg:[&>*]:w-auto lg:[&>*:nth-child(n+5)]:hidden lg:max-xl:[&>*:nth-child(4)]:hidden'

// SSR-compatible data fetching with useAsyncData
// All fetches run in parallel on the server for optimal performance

const { data: mentorData } = await useAsyncData(
    'homepage-mentors',
    () => $fetch('/api/homepage/mentors', { query: { limit: 5 } }),
    { server: true }
)

const { data: jobData } = await useAsyncData(
    'homepage-jobs',
    () => $fetch('/api/homepage/jobs', { query: { limit: 5 } }),
    { server: true }
)

const { data: projectData } = await useAsyncData(
    'homepage-projects',
    () => $fetch('/api/homepage/projects', { query: { limit: 5 } }),
    { server: true }
)

const { data: skillsData } = await useAsyncData(
    'homepage-skills',
    () => $fetch('/api/homepage/skills', { query: { limit: 5 } }),
    { server: true }
)

function toPlainText(value: unknown): string {
    return String(value || '')
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, ' ')
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, ' ')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\s+/g, ' ')
        .trim()
}

function firstPlainText(...values: unknown[]): string {
    for (const value of values) {
        const text = toPlainText(value)
        if (text) return text
    }

    return ''
}

function tagLabel(tag: any): string {
    return typeof tag === 'string' ? tag : tag?.name || tag?.label || tag?.value || ''
}

function getMentorDescription(mentor: any): string {
    const jobDescriptions = Array.isArray(mentor.jobs)
        ? mentor.jobs.map((job: any) => job?.description || job?.desc).filter(Boolean)
        : []

    return firstPlainText(
        mentor.shortProfile,
        mentor.mentorMotivation,
        mentor.mentorProfile?.motivation,
        mentor.hobbiesAndInterests,
        mentor.hobby,
        ...jobDescriptions,
        mentor.jobsText
    )
}

function getJobDescription(job: any): string {
    return firstPlainText(
        job.summary,
        job.description,
        job.highlights,
        job.desc,
        job.challenge,
        job.content,
        job.benefits
    )
}

// Transform data with computed properties
const mentors = computed(() => {
    const data = mentorData.value?.mentors
    if (!data || !Array.isArray(data)) return []
    return data.map((m: any) => {
        // Build job title from jobs array (same as personalized version)
        let jobTitle = ''
        if (m.jobs?.length) {
            const job = m.jobs[0]
            const parts = [job.position, job.org].filter(Boolean)
            if (parts.length) {
                jobTitle = parts.join(' · ')
            }
        }
        // Fallback to jobsText if no structured jobs
        if (!jobTitle && m.jobsText) {
            // jobsText is generated as "position at org"
            jobTitle = m.jobsText.replace(/ at /g, ' · ')
        }

        // Extract tag strings - handle both string arrays and object arrays
        const rawTags = m.mentorInterests || m.interests || []
        const tags = rawTags.slice(0, 5).map(tagLabel).filter(Boolean)

        return {
            name: `${m.firstName || ''} ${m.lastName || ''}`.trim() || 'Unknown',
            title: jobTitle,
            description: getMentorDescription(m),
            imageUrl: m.profileImg || '',
            tags,
            slug: m.slug || m.id
        }
    })
})

const jobs = computed(() => {
    const data = jobData.value?.jobs
    if (!data || !Array.isArray(data)) return []
    return data.map((j: any) => ({
        title: j.title || 'Untitled Job',
        organization: j.orgName || j.orgMeta?.title || j.orgTitle || '',
        description: getJobDescription(j),
        imageUrl: j.image || j.orgImage || j.orgMeta?.image || '',
        imageType: j.image ? 'project' : 'job',
        location: j.geoCity || j.location || '',
        tags: [j.employmentType, ...(j.topics || [])].filter(Boolean).slice(0, 3),
        slug: j.slug || j.id
    }))
})

const volunteers = computed(() => {
    const data = projectData.value?.projects
    if (!data || !Array.isArray(data)) return []
    return data.map((p: any) => ({
        title: p.title || 'Untitled Project',
        organization: p.orgName || p.orgMeta?.title || p.orgTitle || '',
        imageUrl: p.image || p.orgImage || p.orgMeta?.image || '',
        location: p.geoCity || '',
        tags: (p.topics || []).slice(0, 3),
        slug: p.slug || p.id
    }))
})

const educationItems = computed(() => {
    const data = skillsData.value?.skills
    if (!data || !Array.isArray(data)) return []
    return data.map((s: any) => ({
        title: s.title || 'Untitled',
        organization: s.orgName || s.orgMeta?.title || s.orgTitle || '',
        imageUrl: s.coverImg || s.image || s.orgImage || s.orgMeta?.image || '',
        duration: s.duration || '',
        tags: (s.topics || []).slice(0, 3),
        slug: s.slug || s.id
    }))
})
</script>

<style scoped>
/* Hide scrollbar but keep functionality */
.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
    display: none;
}

/* Smooth scrolling */
.overflow-x-auto {
    scroll-behavior: smooth;
}
</style>
